import { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Follow } from "./Follow";

const WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
const MSG = { server: "That didn't send. Email hola@savicoliving.com and we'll hold a seat by hand." };

// Three small squares: copper for taken, copper outline for held, grey outline for open.
const Seats = ({ state }) => (
  <div className="sj-founder-seats" role="img" aria-label={`${state.seats} founder seats, ${state.taken} taken, ${state.held} held.`} data-testid="founder-seat-glyphs">
    {Array.from({ length: state.seats }, (_, i) => {
      const cls = i < state.taken ? "taken" : i < state.taken + state.held ? "held" : "";
      return <span key={i} className={`sj-founder-seat ${cls}`} />;
    })}
  </div>
);

const Counter = ({ state }) => {
  const text = state.sold_out
    ? `${WORDS[state.seats]} of ${WORDS[state.seats]} taken`
    : state.taken ? `${WORDS[state.taken]} of ${WORDS[state.seats]} taken${state.held ? `, ${WORDS[state.held]} held` : ""}`
      : state.held ? `${WORDS[state.held]} of ${WORDS[state.seats]} held` : `${WORDS[state.seats]} seats open`;
  return (
    <div className="sj-founder-count" data-testid="founder-counter">
      <Seats state={state} />
      <span className="sj-founder-count-text" data-testid="founder-counter-text">{text}</span>
    </div>
  );
};

const PledgeForm = ({ state, onDone, onCancel }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", link: "", consent: false, acknowledge: false, website: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Add your name so we know whose seat it is.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(form.email.trim())) return setError("That email doesn't look right. Check it and try again.");
    if (!form.acknowledge) return setError("Tick the box to say you understand what you're committing to.");
    if (!form.consent) return setError("Tick the box so we can email you what happens next.");
    setError("");
    setSending(true);
    const q = new URLSearchParams(window.location.search);
    try {
      const r = await axios.post(`${API}/sojourns/founder/pledge`, {
        ...form, page_url: window.location.href,
        utm_source: q.get("utm_source") || "", utm_medium: q.get("utm_medium") || "", utm_campaign: q.get("utm_campaign") || "",
      });
      onDone(r.data);
    } catch (err) {
      const s = err?.response?.status;
      setError(s === 400 || s === 409 ? err.response.data?.detail || MSG.server : MSG.server);
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="sj-form sj-founder-form" onSubmit={submit} noValidate data-testid="founder-form">
      <div className="sj-field"><label htmlFor="fe-name">Name</label><input id="fe-name" type="text" autoComplete="name" value={form.name} onChange={set("name")} required data-testid="founder-name" /></div>
      <div className="sj-field"><label htmlFor="fe-email">Email</label><input id="fe-email" type="email" autoComplete="email" inputMode="email" value={form.email} onChange={set("email")} required data-testid="founder-email" /></div>
      <div className="sj-field"><label htmlFor="fe-phone">Phone, for the call</label><input id="fe-phone" type="text" autoComplete="tel" inputMode="tel" value={form.phone} onChange={set("phone")} data-testid="founder-phone" /></div>
      <div className="sj-field"><label htmlFor="fe-business">What your business does</label><input id="fe-business" type="text" value={form.business} onChange={set("business")} data-testid="founder-business" /></div>
      <div className="sj-field"><label htmlFor="fe-link">Your LinkedIn or website</label><input id="fe-link" type="text" inputMode="url" value={form.link} onChange={set("link")} data-testid="founder-link" /></div>
      <div className="sj-honey" aria-hidden="true"><label htmlFor="fe-website">Website</label><input id="fe-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></div>
      <label className="sj-check" htmlFor="fe-ack">
        <input id="fe-ack" type="checkbox" checked={form.acknowledge} onChange={set("acknowledge")} required data-testid="founder-acknowledge" />
        <span>I understand the destination, the mentors and the fare are not yet revealed, that my seat is held for {state.hold_hours} hours while we settle it, and that if, after our call, it isn't right, I lose nothing.</span>
      </label>
      <label className="sj-check" htmlFor="fe-consent">
        <input id="fe-consent" type="checkbox" checked={form.consent} onChange={set("consent")} required data-testid="founder-consent" />
        <span>Email me what happens next. Nothing else, and never passed on.</span>
      </label>
      {error && <p className="sj-error" role="alert" data-testid="founder-error">{error}</p>}
      <div className="sj-founder-actions">
        <button type="submit" className="sj-btn" disabled={sending} data-testid="founder-submit">{sending ? "Holding your seat" : "Hold my seat"}</button>
        <button type="button" className="sj-founder-cancel" onClick={onCancel} data-testid="founder-cancel">Not now</button>
      </div>
    </form>
  );
};

const deadline = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" });

// The Founder Edition block: a few seats sold before anything is known. State comes from GET /api/sojourns/founder via the page.
export const FounderEdition = ({ state, setState, onRegister }) => {
  const [mode, setMode] = useState("idle");
  const [done, setDone] = useState(null);

  if (!state || !state.enabled) return null;
  const n = WORDS[state.seats];

  if (done) {
    return (
      <div className="sj-founder is-done" data-testid="founder-done" ref={(el) => el && el.scrollIntoView({ behavior: "smooth", block: "center" })}>
        <p className="sj-label">Founder Edition · Seat {done.seat}</p>
        <h2 className="sj-h2">Seat {done.seat} is held for you.</h2>
        <p className="sj-p">Until {deadline(done.expires_at)}, Madrid time. {done.bank_sent ? "What happens next, and your reference, are in your inbox." : "Steve will write to you personally within a few hours, by reply to the email that's just gone to you."} Once that's settled, the seat is yours and we fix a call.</p>
        <p className="sj-founder-ref" data-testid="founder-reference">Reference <b>{done.reference}</b></p>
        <Follow quiet line="Most of the news lands on Instagram first. Keep an eye on it." testid="founder-done-follow" />
      </div>
    );
  }

  if (state.sold_out) {
    return (
      <div className="sj-founder is-sold" data-testid="founder-sold-out">
        <div className="sj-founder-head">
          <p className="sj-label">Founder Edition · Sold out</p>
          <Counter state={state} />
        </div>
        <h2 className="sj-h2">{n.charAt(0).toUpperCase() + n.slice(1)} seats, taken on blind trust.</h2>
        <p className="sj-p">The first {n} committed before the destination, the mentors or the fare were known. Those are people who know what Savi is about. The remaining seats open on 24 November, by application.</p>
        <button type="button" className="sj-founder-link" onClick={onRegister} data-testid="founder-register-link">Register your interest for the rest</button>
      </div>
    );
  }

  return (
    <div className="sj-founder" data-testid="founder-edition" data-available={state.available}>
      <div className="sj-founder-head">
        <p className="sj-label">Founder Edition</p>
        <Counter state={state} />
      </div>
      <h2 className="sj-h2">{n.charAt(0).toUpperCase() + n.slice(1)} seats, before anything is known.</h2>
      <p className="sj-p">
        {n.charAt(0).toUpperCase() + n.slice(1)} seats go first, to people who will commit before the destination, the mentors or the fare are revealed. For that trust: the founder fare, {state.discount}% below the standard fare, and first pick of rooms. Say yes and the seat is held for you for {state.hold_hours} hours; what happens next comes by email. If, after our call, it isn't right for you, you lose nothing.
      </p>
      {mode === "form"
        ? <PledgeForm state={state} onDone={(d) => { setDone(d); setState((s) => ({ ...s, held: s.held + 1, available: s.available - 1, sold_out: s.available - 1 <= 0, founder_seats: [...s.founder_seats, { seat: d.seat, status: "held" }] })); }} onCancel={() => setMode("idle")} />
        : <button type="button" className="sj-btn sj-founder-btn" onClick={() => setMode("form")} data-testid="founder-open-form">Take a founder seat</button>}
    </div>
  );
};
